import { create } from 'zustand';

/**
 * Authentication state.
 *
 * `status` is a three-state machine, not a boolean, and that matters: on the
 * first paint the client genuinely does not know yet. Collapsing "unknown"
 * into "logged out" is what makes a protected route flash the login page for
 * a moment before redirecting an already-authenticated user back.
 */
export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface PublicUser {
  id: string;
  email: string;
  displayName: string;
  avatarKey: string | null;
}

interface AuthState {
  status: AuthStatus;
  user: PublicUser | null;
  /** Loads the current session. Safe to call more than once. */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

type SetState = (partial: Partial<AuthState>) => void;

/**
 * Coalesces concurrent refreshes.
 *
 * `AppShell` and `RequireAuth` both ask for the session on first paint, and
 * without this the app opens with two identical `/api/me` requests racing
 * each other.
 */
let inFlight: Promise<void> | undefined;

async function loadSession(set: SetState): Promise<void> {
  try {
    // /api/me answers 200 with { user: null } when logged out, so a normal
    // "not signed in" is not an error path.
    const res = await fetch('/api/me', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`/api/me returned ${res.status}`);
    const body = (await res.json()) as { user: PublicUser | null };
    set({ user: body.user, status: body.user ? 'authenticated' : 'anonymous' });
  } catch {
    // A failed probe means we cannot prove a session exists. Treating that as
    // anonymous is the safe direction: worst case the user signs in again.
    set({ user: null, status: 'anonymous' });
  }
}

export const useAuth = create<AuthState>((set) => ({
  status: 'unknown',
  user: null,

  refresh: () => {
    inFlight ??= loadSession(set).finally(() => {
      inFlight = undefined;
    });
    return inFlight;
  },

  logout: async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    set({ user: null, status: 'anonymous' });
  },
}));

/**
 * Starts the Google flow.
 *
 * A full navigation, not fetch: OAuth is a redirect dance through Google's
 * consent screen, and XHR cannot follow it.
 */
export function signInWithGoogle(returnTo: string = window.location.pathname): void {
  const url = new URL('/api/auth/google', window.location.origin);
  if (returnTo && returnTo !== '/login') {
    url.searchParams.set('returnTo', returnTo);
  }
  window.location.assign(url.toString());
}
